import React, { useState, useEffect, useCallback } from 'react';
import {
  ClockIcon,
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import {
  getAuditHistory,
  rollbackTemplate,
  type TaskTemplateRecord,
  type TaskTemplateInheritanceNode,
  type TemplateAuditLogEntry,
} from './templateManagementService';

interface TemplateDetailHistoryProps {
  template: TaskTemplateRecord;
  usageCount: number;
  inheritanceChain: TaskTemplateInheritanceNode[];
  onRolledBack?: () => void;
}

function formatDateTime(value?: string): string {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleString();
}

const ACTION_LABELS: Record<string, string> = {
  created: 'Created',
  updated: 'Updated',
  archived: 'Archived',
  restored: 'Restored',
  deleted: 'Deleted',
  forked: 'Forked',
  cascade_archived: 'Cascade Archived',
  base_edit: 'Base Edit',
  rollback: 'Rollback',
};

const ACTION_COLORS: Record<string, string> = {
  created: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200',
  updated: 'bg-lex-accent-soft text-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent',
  base_edit: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
  archived: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  restored: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
  rollback: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-200',
  forked: 'bg-lex-accent-soft text-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent',
  deleted: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
  cascade_archived: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

const TemplateDetailHistory: React.FC<TemplateDetailHistoryProps> = ({
  template,
  usageCount,
  inheritanceChain,
  onRolledBack,
}) => {
  const [auditEntries, setAuditEntries] = useState<TemplateAuditLogEntry[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [rollbackTarget, setRollbackTarget] = useState<TemplateAuditLogEntry | null>(null);
  const [isRollingBack, setIsRollingBack] = useState(false);
  const [rollbackError, setRollbackError] = useState<string | null>(null);

  const loadAudit = useCallback(async () => {
    try {
      setLoadingAudit(true);
      setAuditError(null);
      const data = await getAuditHistory(template.id, { limit: 50 });
      setAuditEntries(data.entries);
      setTotalEntries(data.total);
    } catch (err) {
      setAuditError(err instanceof Error ? err.message : 'Failed to load audit history');
    } finally {
      setLoadingAudit(false);
    }
  }, [template.id]);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  const handleRollback = async () => {
    if (!rollbackTarget) return;
    try {
      setIsRollingBack(true);
      setRollbackError(null);
      await rollbackTemplate(template.id, rollbackTarget.id);
      setRollbackTarget(null);
      loadAudit();
      onRolledBack?.();
    } catch (err) {
      setRollbackError(err instanceof Error ? err.message : 'Rollback failed');
    } finally {
      setIsRollingBack(false);
    }
  };

  const renderFieldChanges = (entry: TemplateAuditLogEntry) => {
    if (!entry.field_changes || Object.keys(entry.field_changes).length === 0) return null;
    return (
      <div className="mt-2 space-y-1">
        {Object.entries(entry.field_changes).map(([field, diff]) => {
          const d = diff as { old: unknown; new: unknown };
          return (
            <div key={field} className="text-xs text-gray-600 dark:text-gray-400">
              <span className="font-mono text-gray-500">{field}:</span>{' '}
              <span className="text-red-500 line-through">{String(d.old ?? '')}</span>{' '}
              <span className="text-emerald-600">{String(d.new ?? '')}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Metadata */}
      <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Template Metadata</h4>
        <dl className="mt-2 grid grid-cols-2 gap-2 text-sm text-gray-700 dark:text-gray-300">
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Created</dt>
            <dd>{formatDateTime(template.created_at)}</dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Last Updated</dt>
            <dd>{formatDateTime(template.updated_at)}</dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Revision</dt>
            <dd>#{template.revision_number || 1}</dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Usage Count</dt>
            <dd>{usageCount}</dd>
          </div>
          {template.last_edit_reason && (
            <div className="col-span-2">
              <dt className="text-gray-500 dark:text-gray-400">Last Edit Reason</dt>
              <dd className="italic">{template.last_edit_reason}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Inheritance Chain */}
      <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Inheritance Chain</h4>
        {inheritanceChain.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No inheritance chain is configured.</p>
        ) : (
          <ol className="mt-2 space-y-2 text-sm">
            {inheritanceChain.map((node, index) => (
              <li key={node.id} className="rounded-md bg-gray-50 px-3 py-2 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                {index + 1}. {node.name}
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Audit History */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <ClockIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Change History ({totalEntries})
            </h4>
          </div>
          <button
            onClick={loadAudit}
            className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {loadingAudit && (
            <div className="flex items-center justify-center py-6 text-sm text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-lex-accent border-t-transparent mr-2" />
              Loading history...
            </div>
          )}

          {auditError && (
            <div className="p-3 text-sm text-red-600 dark:text-red-400">{auditError}</div>
          )}

          {!loadingAudit && auditEntries.length === 0 && !auditError && (
            <p className="p-4 text-sm text-gray-500 dark:text-gray-400">No audit history available.</p>
          )}

          {auditEntries.map((entry) => (
            <div
              key={entry.id}
              className="border-b border-gray-100 dark:border-gray-700 last:border-b-0"
            >
              <div className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${ACTION_COLORS[entry.action] || 'bg-gray-100 text-gray-800'}`}>
                      {ACTION_LABELS[entry.action] || entry.action}
                    </span>
                    {entry.actor_name && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        by {entry.actor_name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {formatDateTime(entry.created_at)}
                    </span>
                    {entry.previous_state && (
                      <button
                        onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                        className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        title="View details"
                      >
                        <EyeIcon className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {entry.previous_state && (entry.action === 'base_edit' || entry.action === 'updated') && (
                      <button
                        onClick={() => setRollbackTarget(entry)}
                        className="p-1 rounded text-violet-400 hover:text-violet-600 dark:hover:text-violet-300 transition-colors"
                        title="Rollback to this state"
                      >
                        <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {entry.change_reason && (
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 italic">
                    {entry.change_category && (
                      <span className="not-italic font-medium mr-1">
                        [{entry.change_category.replace(/_/g, ' ')}]
                      </span>
                    )}
                    {entry.change_reason}
                  </p>
                )}

                {entry.affected_variant_count > 0 && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    {entry.affected_variant_count} variant{entry.affected_variant_count > 1 ? 's' : ''} affected
                  </p>
                )}

                {expandedEntry === entry.id && renderFieldChanges(entry)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Rollback Confirmation */}
      {rollbackTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 dark:bg-black/70" onClick={() => setRollbackTarget(null)} />
          <div className="relative z-10 w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Confirm Rollback
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              This will restore the template to the state before the{' '}
              <span className="font-medium">{ACTION_LABELS[rollbackTarget.action] || rollbackTarget.action}</span>{' '}
              on {formatDateTime(rollbackTarget.created_at)}.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              The current revision will be incremented and this rollback will be logged.
            </p>

            {rollbackError && (
              <div className="mb-3 p-2 rounded bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
                {rollbackError}
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => { setRollbackTarget(null); setRollbackError(null); }}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRollback}
                disabled={isRollingBack}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isRollingBack ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Rolling back...
                  </>
                ) : (
                  <>
                    <ArrowUturnLeftIcon className="h-4 w-4" />
                    Rollback
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateDetailHistory;
